import { useMemo } from 'react';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { REQUEST_TEMPLATE_TYPEFORM_LINK } from '../../utils';
import { Button, Card, Text } from '../design-system';

import { FeaturedUseCase } from './FeaturedUseCase';
import { GridWrapper, Title } from './sharedStyles';
import { UseCaseGridItem, type UseCaseItem } from './UseCaseGridItem';

const Inner = styled.div`
  text-align: center;
  margin-top: auto;
  margin-bottom: auto;
  padding: 40px 20px;
`;

const StyledCard = styled(Card)`
  cursor: default;
`;

interface UseCaseCategoryGridProps {
  useCases: UseCaseItem[];
}

export const UseCaseCategoryGrid: React.ComponentType<UseCaseCategoryGridProps> =
  React.memo(({ useCases }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.mediaQueryString.LARGE);

    const featuredPost = useMemo(() => {
      return useCases.find(useCase => useCase.featured === true);
    }, [useCases]);

    const remainingPosts = useMemo(() => {
      return useCases.filter(useCase => useCase.slug !== featuredPost?.slug);
    }, [featuredPost?.slug, useCases]);

    return (
      <>
        {featuredPost && !isMobile && (
          <FeaturedUseCase useCase={featuredPost} />
        )}
        <GridWrapper>
          {featuredPost && isMobile && (
            <UseCaseGridItem useCase={featuredPost} />
          )}
          {remainingPosts.map((useCase: UseCaseItem, index: number) => (
            <UseCaseGridItem key={index} useCase={useCase} />
          ))}
          <StyledCard>
            <Inner>
              <Title renderAs="h4">Don't see what you need? </Title>
              <Text size="medium">
                We're always expanding our collection of examples and templates.
                Let us know what you're working on, and we'll whip up an example
                just for you.
              </Text>
              <Button
                link={REQUEST_TEMPLATE_TYPEFORM_LINK}
                size="large"
                stylez="loud"
                text="Request a template"
              />
            </Inner>
          </StyledCard>
        </GridWrapper>
      </>
    );
  });
